import { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Mail, Phone, MapPin, Loader2 } from "lucide-react";
import { COMPANY } from "@/lib/brands";
import useSEO from "@/lib/useSEO";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function Contact() {
  useSEO({
    title: "Contact RSA Overseas — Request a Quote · Kamshet, Pune",
    description:
      "Speak to RSA Overseas Pvt. Ltd. — call +91 78220 86946 or email info@rsaoverseas.com. Warehouse: Kamshet, Pune 410405. We respond within one business day.",
    path: "/contact",
  });
  const [form, setForm] = useState({
    name: "",
    email: "",
    phone: "",
    company: "",
    subject: "",
    message: "",
  });
  const [loading, setLoading] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  const handleChange = (e) =>
    setForm({ ...form, [e.target.name]: e.target.value });

  const onSubmit = async (e) => {
    e.preventDefault();
    if (!form.name.trim() || !form.email.trim() || !form.message.trim()) {
      toast.error("Please fill in your name, email and message.");
      return;
    }
    setLoading(true);
    try {
      const { data } = await axios.post(`${API}/contact`, form);
      toast.success(data.message || "Message sent. We'll be in touch shortly.");
      setSubmitted(true);
      setForm({
        name: "",
        email: "",
        phone: "",
        company: "",
        subject: "",
        message: "",
      });
    } catch (err) {
      const msg =
        err?.response?.data?.detail ||
        "Something went wrong. Please try again or call us directly.";
      toast.error(typeof msg === "string" ? msg : "Submission failed");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div data-testid="contact-page" className="bg-parchment">
      {/* HERO */}
      <section className="relative bg-aubergine text-parchment overflow-hidden">
        <div
          aria-hidden
          className="absolute inset-0 opacity-90"
          style={{
            backgroundImage:
              "radial-gradient(1000px 500px at 20% 30%, rgba(225,29,96,0.24), transparent 60%), radial-gradient(800px 400px at 90% 90%, rgba(147,51,234,0.18), transparent 60%)",
          }}
        />
        <div aria-hidden className="absolute inset-0 grain-overlay" />
        <div className="relative mx-auto max-w-[1400px] px-6 md:px-12 lg:px-16 pt-20 md:pt-28 pb-24 md:pb-32">
          <div className="text-[10px] uppercase tracking-[0.32em] font-semibold text-magenta flex items-center gap-4 mb-10">
            <span className="h-px w-8 bg-magenta/60" />
            Contact
          </div>
          <h1
            data-testid="contact-heading"
            className="font-display font-light text-[52px] sm:text-7xl md:text-8xl lg:text-[144px] tracking-[-0.045em] leading-[0.9] text-parchment text-balance max-w-6xl"
          >
            Tell us about your{" "}
            <span className="italic text-magenta">herd.</span>
          </h1>
          <p className="mt-10 max-w-2xl text-lg text-parchment/70 leading-relaxed">
            Whether you need a single bale or a container shipment, our team
            will respond within one business day with pricing, sample images
            and shipping options.
          </p>
        </div>
      </section>

      {/* FORM + INFO */}
      <section className="bg-parchment">
        <div className="mx-auto max-w-[1400px] px-6 md:px-12 lg:px-16 py-20 md:py-28">
          <div className="grid lg:grid-cols-12 gap-x-16 gap-y-16">
            {/* Contact info */}
            <aside className="lg:col-span-5">
              <div className="text-[10px] uppercase tracking-[0.32em] font-semibold text-magenta flex items-center gap-4">
                <span className="h-px w-8 bg-magenta" />
                Reach us directly
              </div>
              <h2 className="font-display font-light mt-8 text-5xl md:text-6xl tracking-[-0.035em] leading-[0.94] text-aubergine text-balance">
                We pick up.{" "}
                <span className="italic text-magenta">We reply.</span>
                <br />
                We deliver.
              </h2>

              <ul className="mt-14 divide-y divide-aubergine/15 border-y border-aubergine/15">
                <li className="flex gap-6 py-6">
                  <MapPin className="h-6 w-6 shrink-0 text-magenta mt-1" strokeWidth={1.5} />
                  <div>
                    <div className="text-[10px] uppercase tracking-[0.32em] font-semibold text-magenta">
                      Warehouse
                    </div>
                    <div className="mt-2 text-aubergine leading-relaxed">
                      {COMPANY.address}
                    </div>
                  </div>
                </li>
                <li className="flex gap-6 py-6">
                  <Phone className="h-6 w-6 shrink-0 text-magenta mt-1" strokeWidth={1.5} />
                  <div>
                    <div className="text-[10px] uppercase tracking-[0.32em] font-semibold text-magenta">
                      Phone
                    </div>
                    <a
                      href={`tel:${COMPANY.phoneRaw}`}
                      data-testid="contact-phone-link"
                      className="mt-2 block font-display italic text-2xl text-aubergine hover:text-magenta transition-colors"
                    >
                      {COMPANY.phone}
                    </a>
                  </div>
                </li>
                <li className="flex gap-6 py-6">
                  <Mail className="h-6 w-6 shrink-0 text-magenta mt-1" strokeWidth={1.5} />
                  <div>
                    <div className="text-[10px] uppercase tracking-[0.32em] font-semibold text-magenta">
                      Email
                    </div>
                    <a
                      href={`mailto:${COMPANY.email}`}
                      data-testid="contact-email-link"
                      className="mt-2 block font-display italic text-2xl text-aubergine hover:text-magenta transition-colors"
                    >
                      {COMPANY.email}
                    </a>
                  </div>
                </li>
              </ul>

              <div className="mt-10">
                <div className="text-[10px] uppercase tracking-[0.32em] font-semibold text-magenta">
                  Working hours
                </div>
                <div className="mt-3 font-display italic text-2xl text-aubergine">
                  Mon – Sat · 9:00 AM – 7:00 PM IST
                </div>
              </div>
            </aside>

            {/* Form */}
            <div className="lg:col-span-7">
              <div className="bg-aubergine text-parchment p-10 md:p-14 relative overflow-hidden grain-overlay">
                <div
                  aria-hidden
                  className="absolute -top-32 -right-32 h-80 w-80 rounded-full bg-magenta/25 blur-3xl"
                />
                <div className="relative">
                  <div className="text-[10px] uppercase tracking-[0.32em] font-semibold text-magenta/90">
                    Send a message
                  </div>
                  <h2 className="font-display font-light mt-6 text-4xl md:text-5xl tracking-[-0.03em] leading-tight text-parchment">
                    Request a quote.
                  </h2>

                  {submitted ? (
                    <div
                      data-testid="contact-success"
                      className="mt-12 border border-parchment/20 p-10"
                    >
                      <div className="font-display italic text-4xl text-parchment">
                        Thank you.
                      </div>
                      <p className="mt-4 text-parchment/75 leading-relaxed">
                        Your message has been received. Our team will get back
                        to you shortly. For urgent enquiries, call{" "}
                        <a
                          href={`tel:${COMPANY.phoneRaw}`}
                          className="text-magenta font-semibold"
                        >
                          {COMPANY.phone}
                        </a>
                        .
                      </p>
                      <button
                        onClick={() => setSubmitted(false)}
                        data-testid="contact-send-another"
                        className="mt-8 inline-flex items-center gap-3 text-parchment text-[11px] tracking-[0.28em] uppercase font-semibold border-b border-magenta pb-1.5 hover:text-magenta"
                      >
                        Send another message
                      </button>
                    </div>
                  ) : (
                    <form
                      onSubmit={onSubmit}
                      data-testid="contact-form"
                      className="mt-12 space-y-8"
                    >
                      <div className="grid md:grid-cols-2 gap-x-8 gap-y-8">
                        <Field
                          label="Your name"
                          required
                          name="name"
                          value={form.name}
                          onChange={handleChange}
                          testid="contact-input-name"
                        />
                        <Field
                          label="Email"
                          required
                          type="email"
                          name="email"
                          value={form.email}
                          onChange={handleChange}
                          testid="contact-input-email"
                        />
                        <Field
                          label="Phone"
                          name="phone"
                          type="tel"
                          value={form.phone}
                          onChange={handleChange}
                          testid="contact-input-phone"
                        />
                        <Field
                          label="Company / Farm"
                          name="company"
                          value={form.company}
                          onChange={handleChange}
                          testid="contact-input-company"
                        />
                      </div>
                      <Field
                        label="Subject"
                        name="subject"
                        value={form.subject}
                        onChange={handleChange}
                        testid="contact-input-subject"
                      />

                      <label className="block">
                        <span className="block text-[10px] uppercase tracking-[0.28em] font-semibold text-parchment/70 mb-3">
                          Message <span className="text-magenta">*</span>
                        </span>
                        <textarea
                          name="message"
                          required
                          rows={5}
                          value={form.message}
                          onChange={handleChange}
                          data-testid="contact-input-message"
                          placeholder="Tell us about your animals, quantities and timelines…"
                          className="w-full bg-transparent border-b border-parchment/30 px-0 py-3 text-parchment placeholder:text-parchment/30 focus:outline-none focus:border-magenta transition-colors resize-none"
                        />
                      </label>

                      <button
                        type="submit"
                        disabled={loading}
                        data-testid="contact-submit-button"
                        className="group mt-4 inline-flex items-center gap-3 bg-magenta hover:bg-parchment hover:text-aubergine disabled:opacity-60 text-parchment text-[11px] tracking-[0.28em] uppercase font-semibold px-8 py-5 transition-all duration-300"
                      >
                        {loading ? (
                          <>
                            <Loader2 className="h-4 w-4 animate-spin" />
                            Sending…
                          </>
                        ) : (
                          <>
                            Send Message
                            <span className="h-px w-6 bg-current transition-all group-hover:w-10" />
                          </>
                        )}
                      </button>
                    </form>
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}

function Field({ label, name, value, onChange, type = "text", required, testid }) {
  return (
    <label className="block">
      <span className="block text-[10px] uppercase tracking-[0.28em] font-semibold text-parchment/70 mb-3">
        {label} {required && <span className="text-magenta">*</span>}
      </span>
      <input
        name={name}
        type={type}
        required={required}
        value={value}
        onChange={onChange}
        data-testid={testid}
        className="w-full bg-transparent border-b border-parchment/30 px-0 py-3 text-parchment placeholder:text-parchment/30 focus:outline-none focus:border-magenta transition-colors"
      />
    </label>
  );
}
